import {
    Avatar, Box, Chip, IconButton, Input,
    Sheet, Stack, Tooltip, Typography
} from "@mui/joy";

import MenuRoundedIcon          from '@mui/icons-material/MenuRounded';
import SearchRoundedIcon        from '@mui/icons-material/SearchRounded';
import NotificationsRoundedIcon from '@mui/icons-material/NotificationsRounded';
import AddRoundedIcon           from '@mui/icons-material/AddRounded';
import LightModeRoundedIcon     from '@mui/icons-material/LightModeRounded';
import DarkModeRoundedIcon      from '@mui/icons-material/DarkModeRounded';

import { useContext, useState } from "react";
import { LayoutContext } from "../../context";
import { openSidebar } from "../../utils";
import { useLocation } from "react-router";

// ─── Page titles map ──────────────────────────────────────────────────────────
const PAGE_TITLES = {
    "/":           { title: "Дашборд",    sub: "Обзор задач и активности" },
    "/kanban":     { title: "Канбан",     sub: "Доска задач" },
    "/calendar":   { title: "Календарь",  sub: "Планирование событий" },
    "/statistics": { title: "Аналитика",  sub: "Статистика и отчёты" },
    "/payments":   { title: "Платежи",    sub: "Финансы проекта" },
    "/performers": { title: "Исполнители", sub: "Команда проекта" },
    "/chats":      { title: "Чаты",       sub: "Сообщения команды" },
    "/knowledge":  { title: "База знаний", sub: "Документация" },
    "/notes":      { title: "Заметки",     sub: "Личные и рабочие заметки" },
    "/tasks":      { title: "Задачи",     sub: "Задачи на каждый день" },
    "/finances":   { title: "Финансы",    sub: "Доходы, расходы и будущие оплаты" },
};

export default function HeaderLK() {
    const { setSidebarOpen, account, totalUnread, setModalEvent } = useContext(LayoutContext);
    const location = useLocation();
    const [searchOpen, setSearchOpen] = useState(false);

    const pageInfo = PAGE_TITLES[location.pathname] || { title: "TaskFlow", sub: "" };
    const today = new Date().toLocaleDateString("ru-RU", { weekday: "long", day: "numeric", month: "long" });

    return (
        <Sheet
            component="header"
            sx={{
                position: "sticky",
                my: 1,
                top: 8,
                zIndex: 98,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                px: 1,
                height: 55,
                borderRadius: 20,
                
                bgcolor: 'neutral.100',
                backdropFilter: "blur(12px)",
                gap: 2,
            }}
        >
            {/* ── Left: mobile menu + page title ── */}
            <Stack direction="row" alignItems="center" spacing={1.5} sx={{ minWidth: 0 }}>
                {/* Mobile burger */}
                <IconButton
                    onClick={() => { openSidebar(); setSidebarOpen(true); }}
                    variant="plain"
                    color="neutral"
                    size="sm"
                    sx={{ display: { xs: "flex", md: "none" }, flexShrink: 0 }}
                >
                    <MenuRoundedIcon />
                </IconButton>

                {/* Page title — desktop */}
                <Box sx={{ display: { xs: "none", md: "block" }, minWidth: 0 }}>
                    <Typography
                        sx={{
                            fontWeight: 700, fontSize: "1rem",
                            letterSpacing: "-0.2px", lineHeight: 1.1,
                            color: "text.primary",
                        }}
                        noWrap
                    >
                        {pageInfo.title}
                    </Typography>
                    {pageInfo.sub && (
                        <Typography level="body-xs" sx={{ color: "text.tertiary", lineHeight: 1 }} noWrap>
                            {pageInfo.sub}
                        </Typography>
                    )}
                </Box>

                {/* Date badge — desktop */}
                <Chip
                    size="sm"
                    variant="soft"
                    color="neutral"
                    sx={{
                        display: { xs: "none", lg: "flex" },
                        fontSize: "0.7rem", fontWeight: 500,
                        textTransform: "capitalize",
                        borderRadius: "8px",
                    }}
                >
                    {today}
                </Chip>
            </Stack>

            {/* ── Center: search (desktop) ── */}
            <Box sx={{ flex: 1, maxWidth: 340, display: { xs: "none", sm: "block" } }}>
                <Input
                    size="sm"
                    placeholder="Поиск задач, проектов..."
                    startDecorator={<SearchRoundedIcon sx={{ fontSize: 16, opacity: 0.5 }} />}
                    sx={{
                        borderRadius: "10px",
                        bgcolor: "neutral.softBg",
                        border: "1px solid transparent",
                        fontSize: "0.82rem",
                        "--Input-focusedHighlight": "rgba(99,102,241,0.3)",
                        "&:focus-within": {
                            borderColor: "rgba(99,102,241,0.4)",
                            bgcolor: "background.surface",
                            boxShadow: "0 0 0 3px rgba(99,102,241,0.1)",
                        },
                    }}
                />
            </Box>

            {/* ── Right: actions ── */}
            <Stack direction="row" alignItems="center" spacing={0.5} sx={{ flexShrink: 0 }}>

                {/* Search mobile */}
                <IconButton
                    size="sm" variant="plain" color="neutral"
                    sx={{ display: { xs: "flex", sm: "none" } }}
                    onClick={() => setSearchOpen(v => !v)}
                >
                    <SearchRoundedIcon sx={{ fontSize: 19 }} />
                </IconButton>

                {/* New task */}
                {/* <Tooltip title="Новая задача" placement="bottom" size="sm">
                    <IconButton
                        size="sm"
                        variant="soft"
                        color="primary"
                        onClick={() => setModalEvent?.({ event: null, open: true })}
                        sx={{
                            borderRadius: "10px",
                            width: 32, height: 32,
                        }}
                    >
                        <AddRoundedIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                </Tooltip> */}

                {/* Notifications */}
                <Tooltip title="Уведомления" placement="bottom" size="sm">
                    <Box sx={{ position: "relative" }}>
                        <IconButton
                            size="sm" variant="plain" color="neutral"
                            sx={{ borderRadius: "10px", width: 32, height: 32 }}
                        >
                            <NotificationsRoundedIcon sx={{ fontSize: 19 }} />
                        </IconButton>
                        {totalUnread > 0 && (
                            <Box sx={{
                                position: "absolute", top: 4, right: 4,
                                width: 8, height: 8, borderRadius: "50%",
                                bgcolor: "danger.500",
                                border: "2px solid",
                                borderColor: "background.surface",
                            }} />
                        )}
                    </Box>
                </Tooltip>
            </Stack>
        </Sheet>
    );
}